import _ from 'lodash';

const DefinitionLevels = {
  ORGANIZATION: 'organization',
  TEAM: 'team',
  REPOSITORY: 'repository'
};
const getSearchDefinitionFn =
  ({ name, definitionLevel }) =>
  setting =>
    setting.name &&
    name === setting.name &&
    (definitionLevel ? setting.definitionLevel === definitionLevel : true);
export const createGetSettingsMethods = settings => {
  /**
   * @param {object} params
   * @param {string} params.name
   * @param {any} [params.definitionLevel]
   * @param {number} [params.teamId]
   * @param {number} [params.repositoryId]
   * @param {any} [params.key]
   * @param {any} defaultValue
   * @returns {any}
   */
  const getSettingOrDefinitionValue = (
    {
      name,
      definitionLevel = DefinitionLevels.ORGANIZATION,
      teamId,
      repositoryId,
      key
    },
    defaultValue = null
  ) => {
    const resolvedDefinitionLevel =
      definitionLevel === DefinitionLevels.TEAM && !teamId
        ? DefinitionLevels.ORGANIZATION
        : definitionLevel;
    const settingOrDefinition = getSettingOrDefinition({
      name,
      definitionLevel: resolvedDefinitionLevel,
      teamId,
      repositoryId
    });
    return _.get(
      settingOrDefinition,
      key ? `value.${key}` : 'value',
      defaultValue
    );
  };
  const getSettingDefinition = ({ name, definitionLevel }) =>
    settings.find(
      getSearchDefinitionFn({
        name,
        definitionLevel
      })
    );
  const getSettingDefinitions = ({ name, definitionLevel }) =>
    settings.filter(
      getSearchDefinitionFn({
        name,
        definitionLevel
      })
    );
  const getSetting = ({ name, definitionLevel, teamId, repositoryId }) => {
    const definition = getSettingDefinition({ name, definitionLevel }) || {};
    return definition
      ? settings.find(
          setting =>
            definition.id &&
            setting.definitionId === definition.id &&
            (teamId ? setting.teamId === teamId : true) &&
            (repositoryId ? setting.repositoryId === repositoryId : true)
        )
      : null;
  };

  const getSettings = ({ name, definitionLevel, teamId, repositoryId }) => {
    const definitions = getSettingDefinitions({ name, definitionLevel });
    if (!definitions.length) {
      return [];
    }
    const definitionsIds = new Set(definitions.map(({ id }) => id));
    return settings.filter(
      setting =>
        definitionsIds.has(setting.definitionId) &&
        (teamId ? setting.teamId === teamId : true) &&
        (repositoryId ? setting.repositoryId === repositoryId : true)
    );
  };

  function getSettingOrDefinition({
    name,
    definitionLevel,
    teamId,
    repositoryId
  }) {
    const definition = getSettingDefinition({ name, definitionLevel }) || {};
    if (!definition) {
      return null;
    }

    const setting = getSetting({ name, definitionLevel, teamId, repositoryId });

    return setting
      ? setting
      : {
          ...definition,
          value: definition.defaultValue
        };
  }

  const getTeamOrOrgSetting = ({ name, definitionLevel, teamId }) => {
    const orgSetting = getSettingOrDefinitionValue({
      name,
      definitionLevel: DefinitionLevels.ORGANIZATION
    });

    return definitionLevel === DefinitionLevels.TEAM
      ? _.get(
          getSetting({ name, definitionLevel, teamId }),
          'value',
          orgSetting
        )
      : orgSetting;
  };

  const getSettingsByDefinitionLevel = (definitionLevel, id) => {
    const definitions = settings.filter(
      setting => setting.definitionLevel === definitionLevel
    );
    const definitionsById = _.keyBy(definitions, 'id');
    const curDefinitionLevelSettings = settings
      .filter(({ organizationId, repositoryId, teamId }) => {
        switch (definitionLevel) {
          case DefinitionLevels.ORGANIZATION:
            return organizationId === id;
          case DefinitionLevels.REPOSITORY:
            return repositoryId === id;
          case DefinitionLevels.TEAM:
            return teamId === id;
          default:
            return false;
        }
      })
      .map(({ definitionId, value }) => ({
        ...definitionsById[definitionId],
        value
      }));
    const curSettingsById = _.keyBy(curDefinitionLevelSettings, 'id');
    return definitions.map(definition => {
      const setting = curSettingsById[definition.id];
      return setting
        ? { ...definition, ...setting }
        : { ...definition, value: definition.defaultValue };
    });
  };
  return {
    getSettings,
    getSettingDefinition,
    getSettingOrDefinitionValue,
    getTeamOrOrgSetting,
    getSettingsByDefinitionLevel,
    getSetting
  };
};
